import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Dropdown, Helper } from 'dodoc-design-system';
import type { ColumnProps } from 'dodoc-design-system/build/types/Components/Table/Table';
import { useParams } from 'react-router';

import { addUserToGroup } from 'App/redux/appSlice';
import { useDispatch, usePublicGroup, usePublicProfiles } from '_common/hooks';
import { listObjects } from '_common/components/Table/TableSlice';
import { SearchUser, UserAvatar } from '_common/components';
import { ThunksTable } from '_common/components/Table';
import { UserOption } from '_common/components/SearchUser/SearchUser';
import Cell from '_common/components/Table/Cells';
import { openAndUpdateModal } from '_common/modals/ModalsSlice';
import { SettingGroupHeader } from 'Settings/components';

import styles from '../GroupSettingsPage.module.scss';

const PAGE_IDENTITY = 'groups_members';

const MembersView = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { id: groupId }: { id: ObjectId } = useParams();

  const { group } = usePublicGroup(groupId);
  const { profiles } = usePublicProfiles(group.users);

  const [memberSelected, setMemberSelected] = useState<UserOption | undefined>();
  const useIsOwner =
    group.user_permissions.includes('admin') || group.user_permissions.includes('owner');

  const columns = useMemo<ColumnProps[]>(
    () => [
      {
        id: 'avatar',
        header: '',
        width: 6.5 * 8,
      },
      {
        id: 'name',
        header: intl.formatMessage({ id: 'groups.header.name' }),
        flex: true,
        width: 11 * 8,
      },
      {
        id: 'email',
        header: intl.formatMessage({ id: 'storage.modals.user.email' }),
        width: 40 * 8,
      },
      {
        id: 'permissions',
        header: intl.formatMessage({ id: 'groups.header.permissions' }),
        orderable: true,
        width: 20 * 8,
      },
    ],
    [],
  );

  const value = useMemo(() => {
    return group.users
      .filter((userId) => profiles[userId])
      .map((objectId) => {
        const object = profiles[objectId];
        const userIsOwner =
          group.user_permissions.includes('admin') || group.user_permissions.includes('owner');
        const subjectIsOwner = group.owner === objectId;
        const renderPermission = () => {
          let permission = intl.formatMessage({ id: 'groups.header.member' }, { membersCount: 1 });

          if (group.permissions.users[objectId]) {
            if (group.permissions.users[objectId].includes('owner')) {
              permission = intl.formatMessage({ id: 'groups.admin' });
            }
          }

          if (group.owner === objectId) {
            permission = intl.formatMessage({ id: 'groups.header.owner' });
          }

          return permission;
        };
        return {
          prefix: null,
          id: object.id,
          avatar: (
            <Cell testId={`${objectId}-avatar-column`}>
              <UserAvatar userId={objectId} size="medium" />
            </Cell>
          ),
          name: (
            <Cell testId={`${objectId}-name-column`} ellipsis>
              {object.name}
            </Cell>
          ),
          email: <Cell testId={`${objectId}-email-column`}>{object.email}</Cell>,
          permissions: <Cell testId={`${objectId}-permission-column`}>{renderPermission()}</Cell>,
          suffix: userIsOwner && !subjectIsOwner && (
            <Cell testId={`${objectId}-suffix-column`}>
              <Cell.Options
                items={
                  <Dropdown.Item
                    onClick={() => {
                      dispatch(
                        openAndUpdateModal({
                          modal: 'ConfirmationModal',
                          data: {
                            title: 'REMOVE_SOMETHING_QUOTED',
                            titleValues: { label: object.name },
                            message:
                              'REMOVING_THIS_USER__FROM_GROUP_WILL_REVOKE_PERMISSIONS_CONFIRM',
                            confirmButtonTextId: 'global.remove',
                            confirmButtonType: 'danger',
                            cancelButtonTextId: 'global.cancel',
                            actionCode: 'removeUserFromGroup',
                            actionValue: { groupId, userId: objectId },
                            headerType: 'error',
                            cancelButtonShow: true,
                          },
                        }),
                      );
                    }}
                    testId="remove-user-dropdown-item"
                  >
                    <FormattedMessage id="groups.removeUser" />
                  </Dropdown.Item>
                }
                testId={`group-members-${object.id}`}
              />
            </Cell>
          ),
        };
      });
  }, [profiles, group?.users]);

  //#region Table object listing handlers
  const fetchObjects = useCallback(async () => {
    return new Promise<Request.FetchResponse>((resolve) => {
      resolve({
        data: { nodes: Object.values(profiles) },
      });
    });
  }, [profiles]);

  useEffect(() => {
    dispatch(
      listObjects({
        identity: PAGE_IDENTITY,
        fetch: fetchObjects,
        cause: 'INITIAL',
        request: { offset: 0 },
      }),
    );
  }, [profiles]);
  //#endregion

  const handleOnMemberAutocompleteChange = (user: UserOption) => {
    setMemberSelected(user);
  };

  const handleOnClickAddMember = () => {
    if (memberSelected) {
      dispatch(addUserToGroup({ groupId: group.id, userId: memberSelected.value }));
      setMemberSelected(undefined);
    }
  };

  return (
    <>
      {/* Members */}
      <SettingGroupHeader
        style={{ padding: '16px', marginBottom: '2rem' }}
        actions={
          useIsOwner && (
            <div className={styles.autocompleteContainer}>
              <SearchUser
                value={memberSelected}
                valuesToFilter={group.users}
                onChange={handleOnMemberAutocompleteChange}
                placeholder={intl.formatMessage({
                  id: 'settings.group.placeholderAutocomplete',
                })}
                escapeClearsValue
                width="37rem"
                testId="members"
                menuLabel={intl.formatMessage({ id: 'START_TYPING_TO_SEE_THE_LIST_OF_USERS' })}
              />
              <Button
                size="medium"
                variant="primary"
                disabled={!memberSelected}
                onClick={handleOnClickAddMember}
                style={{ marginLeft: '2rem' }}
                testId="members-add-button"
              >
                <FormattedMessage id="global.add" />
              </Button>
            </div>
          )
        }
      >
        <div className={styles.userPermissionsText}>
          <span className={styles.title}>
            {intl.formatMessage({ id: 'settings.group.groupMembersTitle' })}
          </span>
        </div>
      </SettingGroupHeader>

      {group.users.length === 0 ? (
        <Helper margin="2rem 0 0 0" testId="group-settings-page-no-members-helper">
          <FormattedMessage id="groups.emptyList" />
        </Helper>
      ) : (
        <ThunksTable
          identity={PAGE_IDENTITY}
          columns={columns}
          value={value}
          loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
          fetchObjects={fetchObjects}
          testId="members-tab"
          selectable={false}
          renderFooter={undefined}
        />
      )}
    </>
  );
};

export default MembersView;
